import {Box, BoxProps} from "@mantine/core";
import {readLocalStorageValue} from "@mantine/hooks";
import {bffApi} from "../bffApi";
import {LocalStorageKeys} from "../../../types";

type RoleProtectionProps = BoxProps & {
    allowedRoles: number[];
    children: React.ReactNode;
    hideable?: boolean;
};

export const RoleProtection = ({
    allowedRoles,
    children,
    style,
    hideable = true,
    ...boxProps
}: RoleProtectionProps) => {
    const userData = bffApi.endpoints.getUser.useQuery();
    const showDebugIndicators = readLocalStorageValue({
        key: LocalStorageKeys.ShowDebugIndicators,
        defaultValue: false,
    });
    const showInternalFeatures = readLocalStorageValue({
        key: LocalStorageKeys.ShowInternalFeatures,
        defaultValue: false,
    });

    const hasAccess =
        userData.data !== undefined &&
        Number.isInteger(userData.data?.rolleId) &&
        allowedRoles.includes(userData.data.rolleId);

    if (!hasAccess) {
        return null;
    }

    // If hideable is true, only show content when ShowInternalFeatures is enabled
    if (hideable && !showInternalFeatures) {
        return null;
    }

    if (showDebugIndicators) {
        return (
            <Box
                style={{
                    outline: "2px dashed var(--mantine-color-grape-5)",
                    outlineOffset: "2px",
                    ...style,
                }}
                {...boxProps}
            >
                {children}
            </Box>
        );
    }

    return children;
};

export const useRoleProtection = (
    allowedRoles: number[],
    hideable: boolean = true
) => {
    const userData = bffApi.endpoints.getUser.useQuery();
    const showInternalFeatures = readLocalStorageValue({
        key: LocalStorageKeys.ShowInternalFeatures,
        defaultValue: false,
    });

    const hasAccess =
        userData.data !== undefined &&
        Number.isInteger(userData.data?.rolleId) &&
        allowedRoles.includes(userData.data.rolleId);

    if (!hasAccess) {
        return false;
    }

    // If hideable is true, only return true when ShowInternalFeatures is enabled
    if (hideable && !showInternalFeatures) {
        return false;
    }

    return true;
};
